import { getFormats, getTypes, getSizes } from 'forms/creative-form/constants';
/** import single application imports in the section above, Sort Lines Ascending */

/**
 * Forecast-specific wrappers around creative-form constants.
 *
 * PLATFORMS / PLACEMENTS defaults mirror the legacy Elasticsearch forecast UI:
 * in-app platform and interstitial placement. Elasticsearch layout calls
 * getSizesForForecast(creativeFormat) without platform/placement args; Snowflake
 * passes draft.platforms and draft.placements explicitly.
 */

/** Default platform for legacy forecast sizing when caller omits platforms (Elasticsearch). */
const PLATFORMS = ['inapp'];

/** Default placement for legacy forecast sizing when caller omits placements (Elasticsearch). */
const PLACEMENTS = ['interstitial'];

/**
 * Returns creative format options for the forecast form, excluding types with no formats.
 *
 * @param {object|null} exporter - Passed through to getTypes().
 * @param {string[]} [platforms=['inapp']] - Selected platforms from draft; defaults are fallback only.
 * @returns {Array<{formats: Array, ...}>}
 * @remarks Used by both Snowflake and Elasticsearch forecast UIs (layout.js). Layout always passes draft.platforms.
 */
export function getTypesForForecast(exporter, platforms = PLATFORMS) {
    const types = getTypes(platforms, exporter).map(type => ({
        ...type,
        formats: type.formats,
    }));

    return types.filter(type => type.formats.length > 0);
}

/**
 * Returns creative size options for the forecast form.
 *
 * @param {string} type - Creative format value.
 * @param {string[]} [platforms=['inapp']] - Draft platforms (Snowflake). Omitted on Elasticsearch; defaults to in-app.
 * @param {string[]} [placements=['interstitial']] - Draft placements (Snowflake). Omitted on Elasticsearch; defaults to interstitial.
 * @returns {Array<{value: string, label: string}>}
 * @remarks Elasticsearch: layout calls with format only. Snowflake: layout passes draft platforms/placements.
 */
export function getSizesForForecast(type, platforms = PLATFORMS, placements = PLACEMENTS) {
    const sizes = getSizes(platforms, type, placements).map(({ value, label }) => ({
        value,
        label,
    }));

    return sizes;
}

/**
 * Returns creative type options for the forecast form.
 *
 * @param {string} type - Creative format value.
 * @param {string[]} [platforms=['inapp']] - Selected platforms from draft; defaults are fallback only.
 * @returns {Array<{value: string, label: string}>}
 * @remarks Used by both Snowflake and Elasticsearch forecast UIs (layout.js). Layout always passes draft.platforms.
 */
export function getFormatsForForecast(type, platforms = PLATFORMS) {
    const formats = getFormats({ platforms, type }).map(({ value, label }) => ({
        value,
        label,
    }));

    return formats;
}

/** Icon keys for video skippable filter options in the forecast UI. */
export const VIDEO_SKIPPABLE_ICONS = {
    exclude: 'CANCEL',
    include: 'CHECK',
    none: 'NONE',
};

/**
 * Select options for the video skippable filter in the forecast form.
 *
 * @remarks Snowflake only — layout renders this field when canUseVideoSkippable() is true.
 */
export const VIDEO_SKIPPABLE_OPTIONS = [
    {
        label: 'Apply no filter',
        value: '',
        icon: VIDEO_SKIPPABLE_ICONS.none,
        grayColor: true,
    },
    {
        label: 'Exclude skippable videos',
        value: 0,
        icon: VIDEO_SKIPPABLE_ICONS.exclude,
    },
    {
        label: 'Show only skippable videos',
        value: 1,
        icon: VIDEO_SKIPPABLE_ICONS.include,
    },
];

/**
 * Whether the ctr=1 estimate request and min/max range should be used for this creative format.
 *
 * @param {string} creativeFormat - Draft creative format value.
 * @returns {boolean} False for video and native (no estimate range).
 * @remarks Both datasources: updatePrediction skips the ctr=1 HTTP request when false.
 *          Snowflake only: layout passes result to InventoryIndicatorWithDailyPredictionV6 as useEstimateData.
 */
export function showEstimateData(creativeFormat) {
    return !['video', 'native'].includes(creativeFormat);
}
